import { useState, type FormEvent } from "react";

import { Button, TextField } from "@/components";

/**
 * 시안 01의 짝 — 계정 만들기.
 *
 * 초대 코드는 없다. 누구나 만들 수 있다.
 *
 * 주소는 로컬파트만 받는다. 도메인은 서버가 정하는 값이라(`GET /api/config`)
 * 고를 수 있는 것처럼 보이면 안 된다.
 */

export type SignupDraft = {
  localPart: string;
  displayName: string;
  password: string;
};

type SignupCardProps = {
  /** 주소 뒤에 붙는 도메인. 아직 못 받았으면 비워둔다 */
  mailDomain: string;
  pending?: boolean;
  error?: string | null;
  onSubmit: (draft: SignupDraft) => void;
};

/** 서버와 같은 값. 여기서 미리 막아야 다 채우고 나서 400을 보지 않는다. */
const MIN_PASSWORD = 8;

export function SignupCard({
  mailDomain,
  pending = false,
  error,
  onSubmit,
}: SignupCardProps) {
  const [localPart, setLocalPart] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD;
  const ready =
    localPart.trim().length > 0 &&
    displayName.trim().length > 0 &&
    password.length >= MIN_PASSWORD;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!ready || pending) return;

    onSubmit({
      localPart: localPart.trim(),
      displayName: displayName.trim(),
      password,
    });
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex w-full max-w-100 flex-col items-center gap-9 px-6"
    >
      <div className="bg-ink text-paper flex size-18 items-center justify-center rounded-3xl text-3xl font-bold">
        @
      </div>

      <div className="flex flex-col items-center gap-2">
        <h1 className="text-ink text-[28px] font-bold tracking-tight">Cat Mail</h1>
        <p className="text-muted text-center text-[13px] leading-[1.6]">
          쓸 주소를 정하면 시작할 수 있어요.
        </p>
      </div>

      <div className="flex w-full flex-col gap-3">
        <TextField
          size="xl"
          label="쓸 메일 주소"
          labelHidden={false}
          autoComplete="username"
          placeholder="이름"
          value={localPart}
          onChange={(event) => setLocalPart(event.target.value)}
          // 도메인은 고를 수 있는 것이 아니라 정해진 것이다. 칸 안에 붙여 보여준다.
          trailing={
            <span className="text-muted-2 text-[15px]">@{mailDomain || "…"}</span>
          }
          required
        />

        <TextField
          size="xl"
          label="이름"
          labelHidden={false}
          autoComplete="name"
          placeholder="상대에게 보이는 이름"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          required
        />

        <TextField
          size="xl"
          label="비밀번호"
          labelHidden={false}
          type="password"
          autoComplete="new-password"
          placeholder={`${MIN_PASSWORD}자 이상`}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
        />

        {/* 다 채우고 누른 뒤에 알려주면 늦다 */}
        {tooShort ? (
          <p className="text-muted px-1 text-[13px]">
            비밀번호는 {MIN_PASSWORD}자 이상이어야 해요.
          </p>
        ) : null}

        {error ? (
          <p role="alert" className="text-danger px-1 text-sm">
            {error}
          </p>
        ) : null}

        <Button type="submit" size="xl" block disabled={!ready || pending}>
          {pending ? "만드는 중…" : "시작하기"}
        </Button>
      </div>
    </form>
  );
}
